<template>
  <InsideContent
    :title="$t('ADVERTISEMENT.SEARCH_TERMS.TITLE')"
    :description="$t('ADVERTISEMENT.SEARCH_TERMS.DESCRIPTION')"
    class="search-terms"
  >
    <Card
      :class="{
        'search-terms__card--expanded': isExpanded,
        'z-75': isExpanded,
      }"
    >
      <KeywordFilters
        :search-placeholder="$t('ADVERTISEMENT.BIDS.SEARCH_SEARCH_TERMS')"
        :search.sync="filters.search"
        :range.sync="filters.range"
        @update:range="getSearchTerms"
        @update:search="resetSelection"
      />
      <div class="search-terms__actions-row row">
        <div class="search-terms__add-buttons columns medium-6 small-12">
          <MiniAddButtonWithHelp
            class="search-terms__add-button"
            :disabled="!hasSelection"
            :content="$t('ADVERTISEMENT.SEARCH_TERMS.NO_KEYWORD_SELECTED')"
            @click="openAddKeywordModal"
          >
            {{ $t('ADVERTISEMENT.ADD_TO_KEYWORDS') }}
          </MiniAddButtonWithHelp>
          <MiniAddButtonWithHelp
            class="search-terms__add-button"
            :disabled="!hasSelection"
            :content="$t('ADVERTISEMENT.SEARCH_TERMS.NO_KEYWORD_SELECTED')"
            plain
            @click="openAddNegativeKeywordModal"
          >
            {{ $t('ADVERTISEMENT.ADD_TO_NEGATIVE_KEYWORDS') }}
          </MiniAddButtonWithHelp>
        </div>
        <div class="search-terms__actions columns medium-6 small-12">
          <IconicButton
            :text="$t('ADVERTISEMENT.REFRESH')"
            icon="refresh"
            @click="getSearchTerms"
          />
          <FullScreenButton v-model="isExpanded" />
          <KeywordColumnSettings
            :visible-columns.sync="visibleColumns"
            :hidden-columns.sync="hiddenColumns"
            :locked-columns="lockedColumns"
            @update:visible-columns="setVisibleColumns"
          />
        </div>
      </div>
      <CardApiError v-if="showApiError" @retry="getSearchTerms" />
      <KeywordList
        v-else
        :is-loading="loading"
        :bids="filteredData"
        :reset-selection-controller="resetSelectionController"
        :columns="collectedColumns"
        @selection-change="setSelection"
      />
    </Card>
    <AddKeywordFromSearchTermsModal
      v-model="showAddKeywordModal"
      :selections="selections"
    />
    <AddNegativeKeywordFromSearchTermsModal
      v-model="showAddNegativeKeywordModel"
      :selections="selections"
    />
  </InsideContent>
</template>

<script>
import InsideContent from '@@/common/layout/InsideContent';
import Card from '@@/common/Card';
import CardApiError from '@@/common/CardApiError';

import KeywordFilters from '@@/advertisement/common/keyword/KeywordFilters';
import IconicButton from '@@/advertisement/common/IconicButton';
import FullScreenButton from '@@/advertisement/common/FullScreenButton';
import KeywordList from '@@/advertisement/common/KeywordList';
import KeywordColumnSettings from '@@/advertisement/common/keyword/KeywordColumnSettings';
import MiniAddButtonWithHelp from './MiniAddButtonWithHelp';
import columns from './columns';
import AddKeywordFromSearchTermsModal from './AddKeywordFromSearchTermsModal';
import AddNegativeKeywordFromSearchTermsModal from './AddNegativeKeywordFromSearchTermsModal';

import createController from '@/utils/controller';
import campaignId from '@/mixins/advertisement/campaignId';
import searchTermsColumnStorage from './searchTermsColumnStorage';

import { SearchTerms } from '@/api/api';

import {
  AD_CAMPAIGN_SEARCH_TERM_ADD_BID_BUTTON,
  AD_CAMPAIGN_SEARCH_TERM_ADD_NEGATIVE_KEYWORD_BUTTON,
} from '@/constants/gtm-events';

const resetSelectionController = createController('resetSelection');

const visibleColumns = searchTermsColumnStorage.getVisibleColumns();
const hiddenColumns = searchTermsColumnStorage.getHiddenColumns();

export default {
  name: 'SearchTerms',
  components: {
    InsideContent,
    Card,
    KeywordFilters,
    MiniAddButtonWithHelp,
    IconicButton,
    FullScreenButton,
    KeywordList,
    CardApiError,
    KeywordColumnSettings,
    AddKeywordFromSearchTermsModal,
    AddNegativeKeywordFromSearchTermsModal,
  },
  mixins: [campaignId],
  data() {
    return {
      filters: {
        search: '',
        range: 'last-week',
      },
      isExpanded: false,
      searchTerms: [],
      loading: false,
      showApiError: false,
      visibleColumns,
      hiddenColumns,
      showAddKeywordModal: false,
      showAddNegativeKeywordModel: false,
      selections: [],
    };
  },
  computed: {
    filteredData() {
      const { search } = this.filters;
      if (!search) return this.searchTerms;
      return this.searchTerms.filter(searchTerm =>
        searchTerm.query.toLowerCase().includes(search.toLowerCase()),
      );
    },
    collectedColumns() {
      return [...columns.static, ...this.visibleColumns];
    },
    lockedColumns() {
      return columns.static;
    },
    hasSelection() {
      return Boolean(this.selections.length);
    },
    resetSelectionController() {
      return resetSelectionController;
    },
  },
  created() {
    this.getSearchTerms();
  },
  methods: {
    openAddKeywordModal() {
      this.$gtm.trackEvent(AD_CAMPAIGN_SEARCH_TERM_ADD_BID_BUTTON);

      this.showAddKeywordModal = true;
    },
    openAddNegativeKeywordModal() {
      this.$gtm.trackEvent(AD_CAMPAIGN_SEARCH_TERM_ADD_NEGATIVE_KEYWORD_BUTTON);

      this.showAddNegativeKeywordModel = true;
    },
    getSearchTerms() {
      this.loading = true;
      this.showApiError = false;
      return SearchTerms.getSearchTerms({
        campaignId: this.campaignId,
        range: this.filters.range,
      })
        .then(searchTerms => {
          this.searchTerms = searchTerms;
        })
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
          this.showApiError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setVisibleColumns(visibleColumns) {
      searchTermsColumnStorage.setVisibleColumns(visibleColumns);
    },
    setSelection(selections) {
      this.selections = selections;
    },
    resetSelection() {
      resetSelectionController.resetSelection();
    },
  },
};
</script>

<style lang="scss">
.search-terms {
  // To increase specificity
  & &__card {
    &--expanded {
      height: 100vh;
      width: 100vw;
      margin: 0;

      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 99999;

      overflow-y: scroll;
    }
  }

  &__actions-row {
    margin: $global-margin * 1.5 0 $global-margin * 1.5;
    display: flex;
    align-items: center;
  }

  &__add-buttons {
    @include breakpoint(small down) {
      margin-bottom: $global-margin;
    }
  }

  &__add-button + &__add-button {
    @include breakpoint(small down) {
      // zeroize horizontal margin to override el-button + el-button style
      margin: $global-margin/2 0 0 !important;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    @include breakpoint(small down) {
      flex-direction: row;
    }
    @include rtl() {
      .el-button + .el-button {
        margin-left: calc($global-margin / 2);
        margin-right: 0;
      }
    }

    .el-button + .el-button {
      margin-right: calc($global-margin / 2);
      margin-left: 0;
    }
  }
}
</style>
